import { Suspense, useMemo } from 'react';

import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider as MUIThemeProvider } from '@mui/material/styles';
import { Provider as ReduxProvider } from 'react-redux';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';

import {
  AuthPageLayout,
  LoadingWrapper,
  NotFoundMessage,
  ProtectedPageLayout,
} from './components/PageLayout';
import { APP_GROUPS } from './components/PageLayout/apps';
import { SETTINGS_LANDING_HREF } from './components/PageLayout/settings';
import SettingsModal from './components/SettingsModal';
import { SettingsOpenContext } from './components/SettingsModal/context';
import SettingsSection from './components/SettingsModal/SettingsSection';
import useSettingsBackground from './components/SettingsModal/useSettingsBackground';
import { store } from './redux/store';
import getTheme, { ThemeContext, ThemeMode } from './theme';
import { CacheKey } from './utils/common/constants';
import useDarkMode from './utils/react/hooks/useDarkMode';
import lazyWithReload from './utils/react/lazyWithReload';

import { UserGroup } from '@repo/validation';

// Every page is lazy-loaded so each route becomes its own chunk — users only
// download the pages they actually visit (heavy editors/graphs stay out of
// the entry bundle)
const AdminClassPage = lazyWithReload(() => import('./pages/AdminClassPage'));
const AdminCoursePage = lazyWithReload(() => import('./pages/AdminCoursePage'));
const AdminLegacyExamAssignmentPage = lazyWithReload(
  () => import('./pages/AdminLegacyExamAssignmentPage')
);
const AdminLegacyExamPage = lazyWithReload(() => import('./pages/AdminLegacyExamPage'));
const AdminLegacyExamsPage = lazyWithReload(() => import('./pages/AdminLegacyExamsPage'));
const AdminFollowUpPage = lazyWithReload(() => import('./pages/AdminFollowUpPage'));
const AdminGroupPage = lazyWithReload(() => import('./pages/AdminGroupPage'));
const AdminGroupsPage = lazyWithReload(() => import('./pages/AdminGroupsPage'));
const AdminInvoicesPage = lazyWithReload(() => import('./pages/AdminInvoicesPage'));
const AdminLeadPage = lazyWithReload(() => import('./pages/AdminLeadPage'));
const AdminNotesPage = lazyWithReload(() => import('./pages/AdminNotesPage'));
const AdminPayrollPage = lazyWithReload(() => import('./pages/AdminPayrollPage'));
const AdminPeoplePage = lazyWithReload(() => import('./pages/AdminPeoplePage'));
const AdminPermissionPage = lazyWithReload(() => import('./pages/AdminPermissionPage'));
const AdminPermissionsPage = lazyWithReload(() => import('./pages/AdminPermissionsPage'));
const AdminPricePage = lazyWithReload(() => import('./pages/AdminPricePage'));
const AdminPricesPage = lazyWithReload(() => import('./pages/AdminPricesPage'));
const AdminPrintingPage = lazyWithReload(() => import('./pages/AdminPrintingPage'));
const NilePage = lazyWithReload(() => import('./pages/NilePage'));
const AdminReschedulePage = lazyWithReload(() => import('./pages/AdminReschedulePage'));
const AdminRollSummaryPage = lazyWithReload(() => import('./pages/AdminRollSummaryPage'));
const AdminSchoolsPage = lazyWithReload(() => import('./pages/AdminSchoolsPage'));
const AdminStaffPage = lazyWithReload(() => import('./pages/AdminUserPage/AdminStaffPage'));
const AdminStudentPage = lazyWithReload(() => import('./pages/AdminUserPage/AdminStudentPage'));
const AdminSubjectsPage = lazyWithReload(() => import('./pages/AdminSubjectsPage'));
const AdminTimesheetPage = lazyWithReload(() => import('./pages/AdminTimesheetPage'));
const AdminLegacyTopicsPage = lazyWithReload(() => import('./pages/AdminLegacyTopicsPage'));
const AdminVideoPage = lazyWithReload(() => import('./pages/AdminVideoPage'));
const AdminVideosPage = lazyWithReload(() => import('./pages/AdminVideosPage'));
const AnalyticsPage = lazyWithReload(() => import('./pages/AnalyticsPage'));
const AppsPage = lazyWithReload(() => import('./pages/AppsPage'));
const DashboardPage = lazyWithReload(() => import('./pages/DashboardPage'));
const NotesPage = lazyWithReload(() => import('./pages/NotesPage'));
const NotFoundPage = lazyWithReload(() => import('./pages/NotFoundPage'));
const QuizzesPage = lazyWithReload(() => import('./pages/QuizzesPage'));
const ResourcesPage = lazyWithReload(() => import('./pages/ResourcesPage'));
const SettingsAccountPage = lazyWithReload(() => import('./pages/SettingsAccountPage'));
const SignInPage = lazyWithReload(() => import('./pages/SignInPage'));
const StaffClassesPage = lazyWithReload(() => import('./pages/StaffClassesPage'));
const StaffRollPage = lazyWithReload(() => import('./pages/StaffRollPage'));
const StaffStudentActivityLogPage = lazyWithReload(
  () => import('./pages/StaffStudentActivityLogPage')
);
const StudentReschedulePage = lazyWithReload(() => import('./pages/StudentReschedulePage'));
const TeacherClassPage = lazyWithReload(() => import('./pages/TeacherClassPage'));
const TeacherLegacyExamsPage = lazyWithReload(() => import('./pages/TeacherLegacyExamsPage'));
const TeacherLegacyMarkingPage = lazyWithReload(() => import('./pages/TeacherLegacyMarkingPage'));
const TrackerPage = lazyWithReload(() => import('./pages/TrackerPage'));
const TutorialsPage = lazyWithReload(() => import('./pages/TutorialsPage'));
const VideoPlayerPage = lazyWithReload(() => import('./pages/VideoPlayerPage'));
const VideosPage = lazyWithReload(() => import('./pages/VideosPage'));

/**
 * The app's routes, and the settings modal above them. Settings keep their own
 * URLs but open over the last page the reader was on, so the routes behind the
 * modal are matched against that page rather than the settings path itself.
 */
const AppRoutes = () => {
  const location = useLocation();
  const { isSettings, background, closeSettings } = useSettingsBackground();

  return (
    <SettingsOpenContext.Provider value={isSettings}>
      {/* Backstop only: page chunks are caught by the boundary inside
          PageLayout, which keeps the shell up while they load */}
      <Suspense fallback={<LoadingWrapper loading />}>
        {/* The page location object in both branches, so `useRoutes` stays on
            one code path and the page behind the panel is never remounted */}
        <Routes location={isSettings ? background : location}>
          <Route
            path='signin'
            element={
              <AuthPageLayout title='Sign in'>
                <SignInPage />
              </AuthPageLayout>
            }
          />
          <Route
            path=''
            element={
              <ProtectedPageLayout title='Dashboard'>
                <DashboardPage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='notes'
            element={
              <ProtectedPageLayout title='Notes'>
                <NotesPage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='videos'
            element={
              <ProtectedPageLayout title='Videos'>
                <VideosPage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='video/:subject/:videoId'
            element={
              <ProtectedPageLayout title='Videos'>
                <VideoPlayerPage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='resources'
            element={
              <ProtectedPageLayout title='Resources'>
                <ResourcesPage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='quizzes'
            element={
              <ProtectedPageLayout title='Quizzes'>
                <QuizzesPage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='tutorials'
            element={
              <ProtectedPageLayout title='Tutorials'>
                <TutorialsPage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='tracker'
            element={
              <ProtectedPageLayout title='Tracker' authorisedGroups={[UserGroup.Students]}>
                <TrackerPage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='analytics'
            element={
              <ProtectedPageLayout
                title='Analytics'
                authorisedGroups={[UserGroup.Students, UserGroup.Admins, UserGroup.Teachers]}
              >
                <AnalyticsPage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='analytics/:studentId'
            element={
              <ProtectedPageLayout
                title='Analytics'
                authorisedGroups={[UserGroup.Students, UserGroup.Admins, UserGroup.Teachers]}
              >
                <AnalyticsPage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='analytics/:subject/:exam/:studentId/:attempt'
            element={
              <ProtectedPageLayout
                title='Analytics'
                authorisedGroups={[UserGroup.Students, UserGroup.Admins, UserGroup.Teachers]}
                hidePadding
                hideDrawer
              >
                <AnalyticsPage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='analytics/:subject/:exam/:attempt'
            element={
              <ProtectedPageLayout
                title='Analytics'
                authorisedGroups={[UserGroup.Students, UserGroup.Admins, UserGroup.Teachers]}
              >
                <AnalyticsPage />
              </ProtectedPageLayout>
            }
          />
          {/* Gated on the registry, so a caller with no app behind the
                    gallery never reaches it */}
          <Route
            path='apps'
            element={
              <ProtectedPageLayout title='Apps' authorisedGroups={APP_GROUPS}>
                <AppsPage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='admin/subjects'
            element={
              <ProtectedPageLayout title='Subjects' authorisedGroups={[UserGroup.Admins]}>
                <AdminSubjectsPage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='admin/subjects/:subject'
            element={
              <ProtectedPageLayout title='Subjects' authorisedGroups={[UserGroup.Admins]}>
                <AdminSubjectsPage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='admin/invoices'
            element={
              <ProtectedPageLayout title='Invoices' authorisedGroups={[UserGroup.Admins]}>
                <AdminInvoicesPage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='admin/follow-up'
            element={
              <ProtectedPageLayout title='Follow up' authorisedGroups={[UserGroup.Admins]}>
                <AdminFollowUpPage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='admin/people'
            element={
              <ProtectedPageLayout title='People' authorisedGroups={[UserGroup.Admins]}>
                <AdminPeoplePage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='admin/student/:studentId'
            element={
              <ProtectedPageLayout title='Students' authorisedGroups={[UserGroup.Admins]}>
                <AdminStudentPage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='admin/staff/:staffId'
            element={
              <ProtectedPageLayout title='Staff' authorisedGroups={[UserGroup.Admins]}>
                <AdminStaffPage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='admin/lead/:leadType/:leadId'
            element={
              <ProtectedPageLayout title='Leads' authorisedGroups={[UserGroup.Admins]}>
                <AdminLeadPage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='admin/course/:subject/:course'
            element={
              <ProtectedPageLayout title='Courses' authorisedGroups={[UserGroup.Admins]}>
                <AdminCoursePage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='admin/course/:subject/:course/:stream'
            element={
              <ProtectedPageLayout title='Courses' authorisedGroups={[UserGroup.Admins]}>
                <AdminCoursePage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='admin/classes'
            element={
              <ProtectedPageLayout title='Classes' authorisedGroups={[UserGroup.Admins]}>
                <StaffClassesPage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='admin/class/:subject/:classId'
            element={
              <ProtectedPageLayout title='Classes' authorisedGroups={[UserGroup.Admins]}>
                <AdminClassPage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='admin/video/:subject/:videoId'
            element={
              <ProtectedPageLayout title='Videos' authorisedGroups={[UserGroup.Admins]}>
                <AdminVideoPage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='admin/videos/:subject'
            element={
              <ProtectedPageLayout title='Videos' authorisedGroups={[UserGroup.Admins]}>
                <AdminVideosPage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='admin/videos/:subject/:category'
            element={
              <ProtectedPageLayout title='Videos' authorisedGroups={[UserGroup.Admins]}>
                <AdminVideosPage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='admin/videos/:subject/:category/:topic'
            element={
              <ProtectedPageLayout title='Videos' authorisedGroups={[UserGroup.Admins]}>
                <AdminVideosPage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='admin/notes/:subject'
            element={
              <ProtectedPageLayout title='Notes' authorisedGroups={[UserGroup.Admins]}>
                <AdminNotesPage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='admin/notes/:subject/:category'
            element={
              <ProtectedPageLayout title='Notes' authorisedGroups={[UserGroup.Admins]}>
                <AdminNotesPage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='admin/resources/:subject'
            element={
              // TODO: Replace with AdminResourcesPage once no longer stub
              <ProtectedPageLayout title='Resources' authorisedGroups={[UserGroup.Admins]}>
                <ResourcesPage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='admin/legacy-topics/:subject/:group'
            element={
              <ProtectedPageLayout title='Topics' authorisedGroups={[UserGroup.Managers]}>
                <AdminLegacyTopicsPage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='admin/legacy-exams/:subject'
            element={
              <ProtectedPageLayout title='Exams' authorisedGroups={[UserGroup.Admins]}>
                <AdminLegacyExamsPage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='admin/legacy-exams/:subject/:exam'
            element={
              <ProtectedPageLayout title='Exams' authorisedGroups={[UserGroup.Admins]}>
                <AdminLegacyExamPage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='admin/payroll'
            element={
              <ProtectedPageLayout title='Payroll' authorisedGroups={[UserGroup.Managers]}>
                <AdminPayrollPage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='admin/timesheet'
            element={
              <ProtectedPageLayout title='Timesheet' authorisedGroups={[UserGroup.Managers]}>
                <AdminTimesheetPage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='admin/legacy-exam-assignment'
            element={
              <ProtectedPageLayout title='Exam assignment' authorisedGroups={[UserGroup.Admins]}>
                <AdminLegacyExamAssignmentPage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='admin/roll-summary'
            element={
              <ProtectedPageLayout
                title='Roll summary'
                authorisedGroups={[UserGroup.Admins]}
                hideDrawer
                hidePadding
              >
                <AdminRollSummaryPage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='roll'
            element={
              <ProtectedPageLayout
                title='Roll'
                authorisedGroups={[UserGroup.Admins, UserGroup.Teachers]}
              >
                <StaffRollPage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='roll/:subject/:classId/:lesson/reschedule/:studentId'
            element={
              <ProtectedPageLayout title='Roll' authorisedGroups={[UserGroup.Admins]}>
                <AdminReschedulePage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='student/:subject/:classId/:lesson/reschedule'
            element={
              <ProtectedPageLayout title='Reschedule' authorisedGroups={[UserGroup.Students]}>
                <StudentReschedulePage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='teacher/classes'
            element={
              <ProtectedPageLayout title='Classes' authorisedGroups={[UserGroup.Teachers]}>
                <StaffClassesPage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='teacher/class/:subject/:classId'
            element={
              <ProtectedPageLayout title='Roll' authorisedGroups={[UserGroup.Teachers]}>
                <TeacherClassPage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='teacher/legacy-exams'
            element={
              <ProtectedPageLayout
                title='Exams'
                authorisedGroups={[UserGroup.Teachers, UserGroup.Admins]}
              >
                <TeacherLegacyExamsPage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='teacher/legacy-exams/:subject/:examId'
            element={
              <ProtectedPageLayout
                title='Exams'
                authorisedGroups={[UserGroup.Teachers, UserGroup.Admins]}
                hidePadding
                hideDrawer
              >
                <TeacherLegacyExamsPage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='teacher/legacy-marking'
            element={
              <ProtectedPageLayout title='Marking' authorisedGroups={[UserGroup.Teachers]}>
                <TeacherLegacyMarkingPage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='teacher/legacy-marking/:subject/:exam/:student/:attempt'
            element={
              <ProtectedPageLayout
                title='Marking'
                authorisedGroups={[UserGroup.Teachers, UserGroup.Admins]}
                hidePadding
                hideDrawer
              >
                <TeacherLegacyMarkingPage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='student-activity-log'
            element={
              <ProtectedPageLayout
                title='Activity log'
                authorisedGroups={[UserGroup.Teachers, UserGroup.Admins]}
              >
                <StaffStudentActivityLogPage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='admin/printing'
            element={
              <ProtectedPageLayout title='Printing' authorisedGroups={[UserGroup.Admins]}>
                <AdminPrintingPage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='nile'
            element={
              <ProtectedPageLayout title='Nile' authorisedGroups={[UserGroup.Owners]}>
                <NilePage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='*'
            element={
              <ProtectedPageLayout title='Error 404'>
                <NotFoundPage />
              </ProtectedPageLayout>
            }
          />
        </Routes>
      </Suspense>
      {isSettings && (
        <SettingsModal onClose={closeSettings}>
          <Routes>
            <Route path='settings' element={<Navigate replace to={SETTINGS_LANDING_HREF} />} />
            <Route
              path='settings/account'
              element={
                <SettingsSection title='Account'>
                  <SettingsAccountPage />
                </SettingsSection>
              }
            />
            <Route
              path='settings/access/permissions'
              element={
                <SettingsSection title='Permissions' authorisedGroups={[UserGroup.Admins]}>
                  <AdminPermissionsPage />
                </SettingsSection>
              }
            />
            <Route
              path='settings/access/permissions/:tag'
              element={
                <SettingsSection title='Permissions' authorisedGroups={[UserGroup.Admins]}>
                  <AdminPermissionPage />
                </SettingsSection>
              }
            />
            <Route
              path='settings/access/groups'
              element={
                <SettingsSection title='Groups' authorisedGroups={[UserGroup.Admins]}>
                  <AdminGroupsPage />
                </SettingsSection>
              }
            />
            <Route
              path='settings/access/groups/:groupId'
              element={
                <SettingsSection title='Groups' authorisedGroups={[UserGroup.Admins]}>
                  <AdminGroupPage />
                </SettingsSection>
              }
            />
            <Route
              path='settings/schools'
              element={
                <SettingsSection
                  title='Schools'
                  authorisedGroups={[UserGroup.Admins, UserGroup.Managers]}
                >
                  <AdminSchoolsPage />
                </SettingsSection>
              }
            />
            <Route
              path='settings/prices'
              element={
                <SettingsSection title='Prices' authorisedGroups={[UserGroup.Admins]}>
                  <AdminPricesPage />
                </SettingsSection>
              }
            />
            <Route
              path='settings/prices/:priceId'
              element={
                <SettingsSection title='Prices' authorisedGroups={[UserGroup.Admins]}>
                  <AdminPricePage />
                </SettingsSection>
              }
            />
            {/* A settings path naming no section, which the page routes behind
                the modal never see */}
            <Route
              path='*'
              element={
                <SettingsSection title='Error 404'>
                  <NotFoundMessage />
                </SettingsSection>
              }
            />
          </Routes>
        </SettingsModal>
      )}
    </SettingsOpenContext.Provider>
  );
};

const ThemedApp = () => {
  const browserThemeMode = localStorage.getItem(CacheKey.ThemeMode);
  // Prior to reading the user's settings, default to the last theme mode
  // experienced by the browser
  const { darkMode, initDarkMode, initLightMode } = useDarkMode(
    browserThemeMode === ThemeMode.Dark
  );

  const contextValue = useMemo(
    () => ({ initDarkMode, initLightMode }),
    [initDarkMode, initLightMode]
  );

  // Build the theme once per mode change — createTheme is expensive and a new
  // theme identity invalidates every styled component below both providers
  const theme = useMemo(() => getTheme(darkMode ? ThemeMode.Dark : ThemeMode.Light), [darkMode]);

  return (
    <ThemeContext.Provider value={contextValue}>
      <ThemeProvider theme={theme}>
        <MUIThemeProvider theme={theme}>
          <CssBaseline />
          <BrowserRouter>
            <AppRoutes />
          </BrowserRouter>
        </MUIThemeProvider>
      </ThemeProvider>
    </ThemeContext.Provider>
  );
};

const App = () => (
  <ReduxProvider store={store}>
    <ThemedApp />
  </ReduxProvider>
);

export default App;
